import React, { useState, useEffect } from 'react';
import { useHistory, withRouter } from 'react-router';
import { connect, useSelector, useDispatch } from 'react-redux';
import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStop, faPlay } from '@fortawesome/free-solid-svg-icons';
import {
  A11ySettingsPopover,
  ExternalControl
} from '@lwtears/lwt-common-frontend/lib/@a11y/A11ySettings';
import { tA11y } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import { applyA11ySettings } from '@lwtears/lwt-common-frontend/lib/@common/util/a11y-util';
import { ClosedCaptionButton } from '@lwtears/lwt-common-frontend/lib/@a11y/A11ySettings/components/ClosedCaptionButton';
import { useLwtStore } from '@lwtears/lwt-common-frontend/lib/LwtApp';
import AudioCaptions from 'components/AudioCaptions';
import { setAssetsLoaded } from 'store/actions/ui';
import { setAnimationsMode } from 'store/actions/ui';
import { getAudioVendor } from 'utils/accessibilityUtils';

const Navigation = props => {
  const history = useHistory();
  const pathname = history.location.pathname;
  const { animationsModeOn, onGoingAnimation, soundType } = useSelector(state => state.ui);

  const { showBackButton, isLiteVersion, hasNavigation, accessibilityMode } = props;
  const useSimplifiedA11yControls = isLiteVersion && accessibilityMode;

  const [state] = useLwtStore();

  const {
    settings: {
      accessibility: {
        displaySettings: { contentScaling },
        closedCaptionsSettings: { showClosedCaptions }
      }
    }
  } = state;

  const [ariaLabel, setAriaLabel] = useState('');
  const [buttonPressed, setButtonPressed] = useState(false);
  const [showCC, setShowClosedCaptions] = useState(showClosedCaptions || useSimplifiedA11yControls);

  const dispatch = useDispatch();
  const maybeApplyLiteRouteMargin = isLiteVersion
    ? { marginRight: useSimplifiedA11yControls ? 0 : 20 }
    : { marginRight: 120 };

  useEffect(() => {
    switch (pathname) {
      case '/draw-character':
        setAriaLabel(tA11y('goBackLNScreen'));
        break;
      case '/reward-screen':
        setAriaLabel(tA11y('goBackModuleScreen'));
        break;
      default:
        break;
    }
  }, [pathname]);

  useEffect(() => {
    if (buttonPressed) {
      if (animationsModeOn) {
        onGoingAnimation.play();
      } else {
        onGoingAnimation.stop();
      }
      setButtonPressed(false);
    }
  }, [animationsModeOn, buttonPressed]);

  const goBack = () => {
    if (pathname === '/reward-screen') {
      setAssetsLoaded(false);
      sessionStorage.removeItem('moduleType');
      history.push('/modules');
    } else if (pathname === '/draw-character') {
      history.push('/reward-screen');
    } else return false;
  };

  const toggleAnimationsMode = () => {
    setButtonPressed(true);
    dispatch(setAnimationsMode(!animationsModeOn, soundType === 'charInstructions'));
  };

  return (
    hasNavigation && (
      <div id="navigation" className={`navigation zoom${contentScaling}`}>
        <div className={'backButtonDiv'} id="backButtonDiv">
          {showBackButton && !isLiteVersion && (
            <button
              className="wdt-button backButton"
              id="backButton"
              onClick={goBack}
              aria-label={ariaLabel}
            >
              <span className="icon bw_arrow_back" />
            </button>
          )}
        </div>
        {accessibilityMode && (
          <Box
            sx={{
              'display': 'flex',
              'alignItems': 'end',
              'gap': '10px',
              'marginRight': '40px',
              'width': '100%',
              '@media screen and (max-width: 400px)': {
                'gap': '1px',
                'marginRight': '2px',
                'justifyContent': 'center',
                'alignItems': 'center',
                'padding': '10px 0px',
                '& button': {
                  height: '40px !important',
                  width: '40px !important',
                  minWidth: '40px !important'
                }
              },
              '@media screen and (max-width: 200px)': {
                'gap': '0px',
                'marginRight': '0px',
                '& button': {
                  'height': '20px !important',
                  'width': '20px !important',
                  'minWidth': '20px !important',
                  'fontSize': '4px !important',
                  'padding': '3px !important',
                  'margin': '0px !important',

                  '.lwt-common-ccButtonText': {
                    fontSize: '4px !important'
                  }
                }
              }
            }}
          >
            <div id="ccDiv" className="ccDiv">
              {!useSimplifiedA11yControls ? (
                <AudioCaptions />
              ) : (
                showCC && <AudioCaptions useSimplifiedA11yControls />
              )}
            </div>
            <button
              disabled={!onGoingAnimation}
              onClick={toggleAnimationsMode}
              id="animationBtn"
              className="toggleAnimations"
              aria-label={tA11y('toggleAnimationsButton')}
              aria-pressed={!animationsModeOn}
            >
              <FontAwesomeIcon
                icon={animationsModeOn || !onGoingAnimation ? faStop : faPlay}
                style={{ color: '#000000' }}
              />
            </button>
            <div style={{ ...maybeApplyLiteRouteMargin, alignSelf: 'center' }}>
              {!isLiteVersion ? (
                <A11ySettingsPopover
                  externalControls={[ExternalControl.AUDIO_CONTROLS, ExternalControl.CC_BUTTON]}
                  onSubmit={response =>
                    applyA11ySettings(response.data ?? response, getAudioVendor())
                  }
                />
              ) : (
                <ClosedCaptionButton
                  onToggleChange={() => () => setShowClosedCaptions(prev => !prev)}
                  showClosedCaptions={showCC}
                />
              )}
            </div>
          </Box>
        )}
      </div>
    )
  );
};

Navigation.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
    pathname: PropTypes.string
  }),
  history: PropTypes.object,
  hasNavigation: PropTypes.bool.isRequired,
  setAssetsLoaded: PropTypes.func,
  showBackButton: PropTypes.bool,
  isLiteVersion: PropTypes.bool,
  onGoingAnimation: PropTypes.object,
  animationsModeOn: PropTypes.bool,
  accessibilityMode: PropTypes.bool,
  soundType: PropTypes.string
};

const mapStateToProps = state => ({
  hasNavigation: state.ui.navigation.showNavigation,
  showBackButton: state.ui.navigation.showBackButton,
  isLiteVersion: state.ui.isLiteVersion,
  onGoingAnimation: state.ui.onGoingAnimation,
  animationsModeOn: state.ui.animationsModeOn,
  accessibilityMode: state.settings.accessibilityMode,
  soundType: state.ui.soundType
});

const mapActionsToProps = {
  setAssetsLoaded
};

export default connect(mapStateToProps, mapActionsToProps)(withRouter(Navigation));
